<template>
  <div>
    <!-- Filter bar -->
    <b-card
      no-body
      style="margin-bottom: 2rem"
    >
      <b-card-body>
        <b-row align-v="center">
          <b-col
            class="d-flex align-items-center"
            style="gap: 8px;"
          >
            <b-form-select
              v-model="filterType"
              :options="$tOptions(typeOptions)"
              size="sm"
              class="filter-select"
            />
            <b-form-select
              v-model="filterMode"
              :options="$tOptions(modeOptions)"
              size="sm"
              class="filter-select"
            />
            <b-form-select
              v-if="filterMode === 'current'"
              v-model="filterStatus"
              :options="$tOptions(statusOptions)"
              size="sm"
              class="filter-select"
            />
            <b-button
              variant="outline-secondary"
              size="sm"
              class="text-nowrap"
              @click="resetFilters"
            >
              {{ $t('ล้างตัวกรอง') }}
            </b-button>
          </b-col>
          <b-col
            cols="12"
            lg="auto"
            class="d-flex align-items-center agent-row mt-1 mt-lg-0"
            style="gap: 8px; margin-left: auto;"
          >
            <div
              v-if="isMaster"
              class="agent-selector-width"
            >
              <AgentSelector
                v-model="selectedAgent"
                size="sm"
                :placeholder="$t('เลือกตัวแทน...')"
                @change="onAgentChange"
              />
            </div>
            <b-form-checkbox
              v-model="includeDownlines"
              switch
              class="agent-filter-checkbox text-nowrap"
              :disabled="downlinesDisabled"
              @change="fetchData"
            >
              {{ $t('รวมตัวแทนย่อย') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
    </div>

    <!-- Cards grid -->
    <b-row
      v-else
      class="match-height"
      style="row-gap: 2rem"
    >
      <b-col
        v-for="item in filteredItems"
        :key="item.id"
        xl="4"
        md="6"
        sm="12"
      >
        <result-card
          :item="item"
          :use-last-result="useLastResult"
          style="margin-bottom: 0"
          @open-report="openReport(item, $event)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable */
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormSelect,
  BFormCheckbox,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import lottoService from '@/service/lottoService'
import reportService from '@/service/reportService'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import ResultCard from '@/views/dashboard/result/ResultCard.vue'

const FILTER_STORAGE_KEY = 'result-dashboard-filters'

// ตัวกรองผูกกับบัญชี ไม่ใช่กับเบราว์เซอร์ — เครื่องเดียวสลับล็อกอินได้
// ถ้าใช้คีย์ร่วมกัน ค่าที่ master ตั้งไว้จะไปทับค่าตั้งต้นของตัวแทนที่ล็อกอินทีหลัง
function storageKeyFor(username) {
  return username ? `${FILTER_STORAGE_KEY}:${username}` : FILTER_STORAGE_KEY
}
function loadSavedFilters(username) {
  try {
    const stored = localStorage.getItem(storageKeyFor(username))
    return stored ? JSON.parse(stored) : null
  } catch { return null }
}
function saveFilters(username, filters) {
  localStorage.setItem(storageKeyFor(username), JSON.stringify(filters))
}

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormSelect,
    BFormCheckbox,
    BButton,
    BSpinner,
    AgentSelector,
    ResultCard,
  },
  data() {
    const saved = loadSavedFilters(this.$store.state.app.userData?.username)

    // ตัวแทนทั่วไปเริ่มที่ "รวมสาย" เพราะยอดของตัวแทนในธุรกิจนี้คือทั้งสายของเขา
    // ไม่ใช่เฉพาะสมาชิกที่ห้อยตรง ๆ — HuayResultSummary เก็บแยกตามตัวแทนชั้นล่างสุด
    // ที่เป็นเจ้าของสมาชิกคนแทงเท่านั้น ตัวแทนชั้นบนจึงไม่มีแถวของตัวเองเลยสักรอบ
    //
    // master ไม่เปลี่ยน: ไม่เลือกตัวแทน = เห็นทั้งระบบอยู่แล้ว การติกของ master
    // จึงเป็นการขยายขอบเขตของ "ตัวแทนที่เลือก" ซึ่งควรเริ่มจากแคบไปกว้าง
    //
    // `??` ไม่ใช่ `||` เพราะค่าที่เคยบันทึกไว้เป็น false ต้องแยกจาก "ยังไม่เคยตั้ง"
    const isMasterUser = this.$store.state.app.isMaster(this.$store)

    return {
      items: [],
      loading: true,
      filterType: null,
      filterMode: 'latest',
      filterStatus: null,
      interval: null,
      selectedAgent: saved?.selectedAgent || null,
      includeDownlines: saved?.includeDownlines ?? !isMasterUser,
      // เก็บเป็นข้อความไทยดิบ ห้ามเรียก $t() ตรงนี้ — data() ทำงานครั้งเดียวตอนสร้าง
      // component ผลลัพธ์จึงถูกแช่ไว้ ถ้าตอนนั้น locale ยังเป็น en ค่าที่ได้จะเป็น
      // 'All types' ซึ่งไม่ใช่ key ในพจนานุกรมอีกต่อไป $tOptions เลยแปลกลับไม่ได้ตลอดกาล
      // ปล่อยให้ $tOptions ใน template เป็นคนแปล มันจะ re-render ตาม locale ให้เอง
      typeOptions: [
        { value: null, text: 'ทุกประเภท' },
        { value: 1, text: 'รัฐบาล' },
        { value: 2, text: 'ธนาคาร' },
        { value: 'stock', text: 'หุ้น' },
        { value: 5, text: 'เลขชุด' },
      ],
      modeOptions: [
        { value: 'latest', text: 'ผลรอบล่าสุด' },
        { value: 'current', text: 'รอบปัจจุบัน' },
      ],
      statusOptions: [
        { value: null, text: 'ทุกสถานะ' },
        { value: 1, text: 'เปิดรับแทง' },
        { value: 3, text: 'ปิดรับแทง' },
        { value: 7, text: 'กำลังจ่าย' },
        { value: 9, text: 'จ่ายเสร็จ' },
      ],
    }
  },
  computed: {
    isMaster() {
      return this.$store.state.app.isMaster(this.$store)
    },
    useLastResult() {
      return this.filterMode === 'latest'
    },
    // master ต้องเจาะจงตัวแทนก่อนถึงจะรวมสายได้ — ยังไม่เลือกคือดูทั้งระบบอยู่แล้ว
    // ตัวแทนทั่วไปมีขอบเขตเดียวคือสายตัวเอง กดได้ทันที
    downlinesDisabled() {
      if (!this.isMaster) return false
      return !this.selectedAgent || !this.selectedAgent.code || this.selectedAgent.code === 'master'
    },
    // ส่งขึ้น API และต่อไปหน้ารายงานเมื่อสวิตช์ "มีผลจริง" เท่านั้น
    // ไม่งั้น master ที่ยังไม่เลือกตัวแทนจะส่งค่าที่ตัวเองกดไม่ได้ขึ้นไปด้วย
    scopeIncludesDownlines() {
      return this.includeDownlines && !this.downlinesDisabled
    },
    filteredItems() {
      return this.items
        .filter(item => {
          if (this.filterType !== null) {
            if (this.filterType === 'stock') {
              if (![3, 4].includes(item.type)) return false
            } else if (item.type !== this.filterType) return false
          }
          if (this.filterMode === 'current' && this.filterStatus !== null && item.status !== this.filterStatus) return false
          return true
        })
        .sort((a, b) => {
          if (this.useLastResult) {
            return this.parseDate(b.lastResult ? b.lastResult.roundDate : b.roundDate)
              - this.parseDate(a.lastResult ? a.lastResult.roundDate : a.roundDate)
          }
          const aHasResult = a.status >= 5 ? 1 : 0
          const bHasResult = b.status >= 5 ? 1 : 0
          if (bHasResult !== aHasResult) return bHasResult - aHasResult
          return this.parseDate(b.roundDate) - this.parseDate(a.roundDate)
        })
    },
  },
  watch: {
    selectedAgent() { this.persistFilters() },
    includeDownlines() { this.persistFilters() },
  },
  async mounted() {
    if (!this.isMaster) {
      await this.fetchData()
    }
    this.interval = setInterval(() => {
      this.pollStatus()
    }, 10000)
  },
  destroyed() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },
  methods: {
    persistFilters() {
      saveFilters(this.$store.state.app.userData?.username, {
        selectedAgent: this.selectedAgent,
        includeDownlines: this.includeDownlines,
      })
    },
    buildAgentParams() {
      const params = new URLSearchParams()
      if (this.isMaster && this.selectedAgent && this.selectedAgent.code) {
        params.set('agent', this.selectedAgent.code)
      }
      if (this.scopeIncludesDownlines) {
        params.set('includeDownlines', 'true')
      }
      return params.toString()
    },
    onAgentChange() {
      if (!this.selectedAgent || !this.selectedAgent.code || this.selectedAgent.code === 'master') {
        this.includeDownlines = false
      }
      this.fetchData()
    },
    async fetchData() {
      this.loading = true
      try {
        const agentParams = this.buildAgentParams()
        const url = agentParams ? `dashboard/summary?${agentParams}` : 'dashboard/summary'
        const res = await reportService.getAuthen(url)
        if (res.status === 200) {
          this.items = res.data
        }
      } finally {
        this.loading = false
      }
    },
    pollStatus() {
      if (document.hidden || this.items.length === 0) return
      lottoService
        .getAuthen('huay/states')
        .then(res => {
          if (res.status === 200) {
            for (let i = 0; i < this.items.length; i += 1) {
              const newStatus = res.data[this.items[i].id]
              if (newStatus !== undefined && this.items[i].status !== newStatus) {
                this.items[i].status = newStatus
              }
            }
          }
        })
        .catch(() => {})
    },
    parseDate(dateStr) {
      if (!dateStr) return 0
      // format: "dd/MM/yyyy HH:mm:ss"
      const [d, m, y] = dateStr.split(' ')[0].split('/')
      return new Date(`${y}-${m}-${d}`).getTime() || 0
    },
    resetFilters() {
      this.filterType = null
      this.filterMode = 'latest'
      this.filterStatus = null
    },
    openReport(item, view) {
      const round = this.useLastResult && item.lastResult ? item.lastResult : item
      if (!round.roundId) return
      if (round.roundId === item.roundId && Number(item.status) !== 9) return
      if (round.roundId !== item.roundId && round.status != null && Number(round.status) !== 9) return

      const query = {
        view,
        huayName: item.name,
        roundDate: round.roundDate || item.roundDate || '',
        huayType: item.type,
      }

      if (this.isMaster && this.selectedAgent && this.selectedAgent.code) {
        query.agent = this.selectedAgent.code
      }
      // ต้องส่งต่อขอบเขตเดียวกับที่การ์ดใช้ ไม่งั้นการ์ดโชว์ยอดทั้งสาย
      // แต่กดเข้าไปเห็นเฉพาะสมาชิกตรง ตัวเลขสองหน้าไม่ตรงกันโดยไม่มีอะไรบอก
      if (this.scopeIncludesDownlines) {
        query.includeDownlines = 'true'
      }

      this.$router.push({
        name: 'dashboard-result-report',
        params: {
          huayId: item.id,
          roundId: round.roundId,
        },
        query,
      })
    },
  },
}
</script>

<style scoped>
.filter-select {
  width: 100%;
  max-width: 200px;
}
.agent-selector-width {
  min-width: 250px;
  max-width: 350px;
  flex: 1;
}
.agent-selector-width >>> .vs__selected {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.agent-selector-width >>> .vs__dropdown-menu {
  min-width: 280px;
}
.agent-selector-width >>> .vs__dropdown-option {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 450px) {
  .agent-row {
    flex-wrap: wrap;
  }
  .agent-selector-width {
    width: 100%;
  }
  .agent-row .agent-filter-checkbox {
    margin-left: auto;
  }
}
.agent-filter-checkbox {
  margin-bottom: 0;
}
.agent-filter-checkbox >>> .custom-control-label {
  margin-bottom: 0;
}
</style>
